import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule, ButtonOptions } from '@usitsdasdesign/dds-ng/button';
import { DropdownOptions, DropdownItemOptions, DropdownModule } from '@usitsdasdesign/dds-ng/dropdown';
import { ExtThemes, ButtonKind, Size, WidthState } from '@usitsdasdesign/dds-ng/shared';
import { SpecificationsService } from '../../core/services/specifications.service';
import { Specification } from '../../core/models/specification.models';
import { AceConfigInterface, AceModule } from 'ngx-ace-wrapper';
import { TabsModule } from '@usitsdasdesign/dds-ng/tabs';
import { getSpecificationLabel } from '../../core/utils/label.utils';
import SwaggerUI from 'swagger-ui-dist/swagger-ui-es-bundle';
import * as yaml from 'js-yaml';

@Component({
  selector: 'app-api-specifications',
  standalone: true,
  imports: [ButtonModule, TabsModule, DropdownModule, AceModule],
  templateUrl: './api-specifications.component.html',
  styleUrl: './api-specifications.component.scss',
})
export class ApiSpecificationsComponent {
  projectId: string;
  specifications: Specification[];
  currentSpecificationId: string;
  specificationDescription: string;
  specificationContent: string;
  uploadContent: string;
  showPreview: boolean;
  editorMode: string = 'text';
  @ViewChild('swagger', { static: false }) container!: ElementRef;
  previewRendered: boolean;

  config: AceConfigInterface = {
    mode: 'text',
    theme: 'github',
    readOnly: true,
    showPrintMargin: false,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private specificationsService: SpecificationsService,
  ) {}

  createSpecificationButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primaryLoud,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_plus',
    isIconLeft: false,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Add a new API Specification',
    customClass: '',
    role: 'button',
  };

  createSpecificationClick(): void {
    this.router.navigate(['create-specification'], { relativeTo: this.route.parent });
  }

  dropdownOptions: DropdownOptions = {
    label: '',
    ariaLabel: '',
    theme: ExtThemes.dark,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isColorBg: false,
    isInverse: false,
    customClass: '',
    disabled: false,
    isResponsive: false,
    stickerIsOutsideClick: false,
    stickerShift: 0,
    stickerMaxHeight: '',
    stickerIsDisabled: false,
    stickerPosition: 'bottom-left',
    stickerIndent: 0,
    stickerCustomClass: '',
    stickerIsDynamic: true,
  };

  dropdownItems: DropdownItemOptions[];

  itemSelected(value: string) {
    this.setSpecificationContent(value);
  }

  uploadButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: '',
    isIconLeft: true,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Upload a new version',
    customClass: '',
    role: 'button',
  };

  uploadButtonClick(): void {
    if (this.currentSpecificationId) {
      this.router.navigate(['update-specification', this.currentSpecificationId], { relativeTo: this.route.parent });
    }
  }

  sourceButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.silent,
    size: Size.lg,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_file',
    isIconLeft: true,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Source',
    customClass: '',
    role: 'button',
  };

  sourceClick(): void {
    this.showPreview = false;
    this.sourceButtonOptions.kind = ButtonKind.primaryLoud;
    this.previewButtonOptions.kind = ButtonKind.silent;
    this.previewRendered = false;
  }

  previewButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.silent,
    size: Size.lg,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_list__l__stroke',
    isIconLeft: true,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Preview',
    customClass: '',
    role: 'button',
  };

  previewClick(): void {
    this.showPreview = true;
    this.sourceButtonOptions.kind = ButtonKind.silent;
    this.previewButtonOptions.kind = ButtonKind.primaryLoud;
  }

  copyButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.silent,
    size: Size.lg,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_copy__l__stroke',
    isIconLeft: false,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Copy',
    customClass: '',
    role: 'button',
  };

  copyClick(): void {
    if (this.specificationContent) {
      navigator.clipboard.writeText(this.specificationContent).then(
        () => {
          console.log('Specification content copied to clipboard');
          // Show success indicator by swapping icon
          this.copyButtonOptions = {
            ...this.copyButtonOptions,
            icon: 'dds-icon_checkmark__l__stroke',
          };

          // Reset icon back to copy after 2 seconds
          setTimeout(() => {
            this.copyButtonOptions = {
              ...this.copyButtonOptions,
              icon: 'dds-icon_copy__l__stroke',
            };
          }, 2000);
        },
        (err) => {
          console.error('Failed to copy specification content:', err);
        },
      );
    }
  }

  ngOnInit(): void {
    this.specifications = [];
    this.dropdownItems = [];
    this.showPreview = false;
    this.previewRendered = false;
    this.sourceButtonOptions.kind = ButtonKind.primaryLoud;
    this.previewButtonOptions.kind = ButtonKind.silent;

    this.route.parent?.paramMap.subscribe((params) => {
      this.projectId = params.get('id') ?? '';

      this.specificationsService.getSpecificationsForProject(this.projectId).subscribe((response) => {
        this.specifications = response == null ? [] : response;
        this.currentSpecificationId = this.specifications[0]?.id ?? '';
        this.specificationContent = this.specifications[0]?.content ?? '';
        this.specificationDescription = this.specifications[0]?.description;
        this.populateDropdown();
        this.detectAndSetEditorMode();
      });
    });
  }

  ngAfterViewChecked() {
    this.renderPreview();
  }

  private populateDropdown(): void {
    if (this.specifications.length > 0) {
      this.dropdownOptions.label = getSpecificationLabel(this.specifications[0]);

      for (let specification of this.specifications) {
        this.dropdownItems.push({
          heading: getSpecificationLabel(specification),
          value: specification.id,
        });
      }
    }
  }

  private setSpecificationContent(id: string) {
    let specification = this.specifications.find((x) => x.id == id);
    if (specification == undefined) return;
    this.currentSpecificationId = specification.id;
    this.specificationContent = specification.content ?? '';
    this.specificationDescription = specification.description;
    this.dropdownOptions.label = getSpecificationLabel(specification);
    this.detectAndSetEditorMode();
  }

  private detectAndSetEditorMode() {
    // Detect if the content is XML or YAML
    const trimmedContent = this.specificationContent.trim();

    // Check if it starts with XML declaration or tag
    if (trimmedContent.startsWith('<?xml') || trimmedContent.startsWith('<')) {
      this.editorMode = 'xml';
    }
    // Check for YAML patterns (starts with key:value or ---)
    else if (trimmedContent.startsWith('---') || /^[a-zA-Z_][\w]*:\s*.+/m.test(trimmedContent)) {
      this.editorMode = 'yaml';
    }
    // Default to text mode
    else {
      this.editorMode = 'text';
    }
  }

  private renderPreview() {
    if (!this.container || this.previewRendered) return; // Render only if swagger container exists and not yet rendered

    this.container.nativeElement.innerHTML = ''; // Clear previous UI
    SwaggerUI({
      domNode: this.container.nativeElement,
      spec: yaml.load(this.specificationContent),
    });
    this.previewRendered = true;
  }
}
